import { Injectable, Inject } from '@angular/core';
import { ApiError } from '@modules/addresses-store/client/src';
import { LOGGER } from '@modules/core';
import { TicketsAndFaresStoreFacade } from '@modules/ticket-fares-store/facade';
import { TicketsAndFaresService } from '@modules/ticket-fares-store/services/tickets-and-fares.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { LoggingService } from '@nx-ng/types';
import { of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { FareSelectorActions } from '../../actions';
import { FareSelectorList, FareSelectorRequest } from '../../client/src';

@Injectable()
export class FareSelectorEffects {
  constructor(
    private readonly actions$: Actions,
    private ticketsAndFaresFacade: TicketsAndFaresStoreFacade,
    private ticketsAndFaresService: TicketsAndFaresService,
    @Inject(LOGGER) private logger: LoggingService
  ) {}

  loadTicketSelectors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FareSelectorActions.loadTicketSelectors),
      switchMap(() =>
        this.ticketsAndFaresFacade.fareSelectorRequest$.pipe(take(1))
      ),
      switchMap((fareSelectorRequest: FareSelectorRequest) => {
        return this.ticketsAndFaresService
          .loadFareSelectors(fareSelectorRequest)
          .pipe(
            map((value: FareSelectorList, _index: number) => {
              if (!value) {
                return FareSelectorActions.loadTicketSelectorsFailure({
                  error: 'No selectors have been retrieved',
                  retryAction: FareSelectorActions.loadTicketSelectors(),
                });
              }
              return FareSelectorActions.loadTicketSelectorsSuccess({
                fareSelectorList: {
                  customerTypes: value.customerTypes,
                  travelMethods: value.travelMethods,
                  travelTimes: value.travelTimes,
                  zones: value.zones,
                },
              });
            }),
            catchError((err) => {
              const apiError: ApiError | string =
                err?.response?.parsedBody || err;

              this.logger.logApiError(
                {
                  apiError,
                },
                err
              );
              return of(
                FareSelectorActions.loadTicketSelectorsFailure({
                  error: apiError,
                  retryAction: FareSelectorActions.loadTicketSelectors(),
                })
              );
            })
          );
      })
    )
  );

  selectorsReloadRequired = createEffect(() =>
    this.actions$.pipe(
      ofType(FareSelectorActions.setCustomerType, FareSelectorActions.setZone),
      map(() => FareSelectorActions.loadTicketSelectors())
    )
  );
}
